import color from "color";
import { MultiPolygon, Point, Polygon } from "ol/geom";
import { fromLonLat } from "ol/proj";
import { useEffect, useMemo } from "react";
import { renderToString } from 'react-dom/server';
import { RFeature, RLayerVector, RStyle } from "rlayers";
import { RFill, RStroke, RText } from "rlayers/style";
import { useLayerManager } from "./LayerManager";
import { LeafletMarker } from "../leaflet/LeafletMarker";
import { LeafletPolygon } from '../leaflet/LeafletPolygon';
import { useMapZones } from "../../reducers/mapZoneSlice";
import { useTranslation } from "react-i18next";

const OLZone = ({ zone, zIndex }) => {
    const { t } = useTranslation();
    const { registerLayer, unregisterLayer, isVisible, getOpacity } = useLayerManager();
    const id = 'zone_' + zone.id;

    useEffect(() => {
        registerLayer(id, zone.name, zone.group ?? t('zone', { count: 100 }), 'Zone', zone.visible, 1);
        return () => unregisterLayer(id);
    }, [id, registerLayer, t, unregisterLayer, zone]);

    const poly = useMemo(() => {
        if (Array.isArray(zone.zone)) {
            return new Polygon([zone.zone.map(lonlat => fromLonLat(lonlat))]);
        }
        if (zone.zone.type === 'MultiPolygon') {
            return new MultiPolygon(zone.zone.coordinates.map(polygon =>
                polygon.map(ring => ring.map(lonlat => fromLonLat(lonlat)))
            ));
        }
        return new Polygon(zone.zone.coordinates.map(ring => ring.map(lonlat => fromLonLat(lonlat))));
    }, [zone.zone]);
    const center = useMemo(() => {
        if (Array.isArray(zone.zone)) {
            return new Point(fromLonLat(zone.zone[Math.floor(zone.zone.length / 2)]));
        }
        if (zone.zone.type === 'MultiPolygon') {
            // Use centroid of the first polygon's outer ring
            const coords = zone.zone.coordinates[0][0];
            const lon = coords.reduce((s, c) => s + c[0], 0) / coords.length;
            const lat = coords.reduce((s, c) => s + c[1], 0) / coords.length;
            return new Point(fromLonLat([lon, lat]));
        }
        const coords = zone.zone.coordinates[0];
        const lon = coords.reduce((s, c) => s + c[0], 0) / coords.length;
        const lat = coords.reduce((s, c) => s + c[1], 0) / coords.length;
        return new Point(fromLonLat([lon, lat]));
    }, [zone.zone]);

    const visible = isVisible(id);
    const opacity = getOpacity(id);

    const zoneColor = color(zone.color);
    const fillColor = color(zoneColor).alpha(zone.opacity);
    const textColor = zoneColor.isDark() ? zoneColor : color(zoneColor).lightness(1 - zoneColor.lightness());


    return <RLayerVector properties={{ title: zone.name, group: zone.group }} visible={visible} opacity={opacity} zIndex={zIndex}>
        <RStyle.RStyle>
            <RStroke color={zone.color} width={zone.width}></RStroke>
            <RFill color={fillColor.hexa()}></RFill>
        </RStyle.RStyle>
        <RFeature geometry={center}>
            <RStyle.RStyle>
                <RText text={zone.name} font="bold 16px Arial" overflow={true}>
                    <RFill color={textColor.hexa()}></RFill>
                </RText>
            </RStyle.RStyle>
        </RFeature>
        <RFeature properties={{ altitudeMode: 'clampToGround' }} geometry={poly}></RFeature>
    </RLayerVector>;
};
const LeafletZone = ({ zone, zIndex }) => {
    const zoneColor = color(zone.color);

    const poly = useMemo(() => {
        if (Array.isArray(zone.zone)) {
            return zone.zone.map(([lon, lat]) => [lat, lon]);
        }
        if (zone.zone.type === 'MultiPolygon') {
            return zone.zone.coordinates.map(polygon =>
                polygon.map(ring => ring.map(([lon, lat]) => [lat, lon]))
            );
        }
        return zone.zone.coordinates[0].map(([lon, lat]) => [lat, lon]);
    }, [zone.zone]);

    const center = useMemo(() => 
        window.L.latLngBounds(
            Array.isArray(zone.zone) || zone.zone.type === 'Polygon' ? poly : poly.flat()
        ).getCenter(), 
    [poly, zone.zone]);

    const textIcon = useMemo(() => {
        const textColor = zoneColor.isDark() ? zoneColor : color(zoneColor).lightness(1 - zoneColor.lightness());
        return new window.L.DivIcon({
            html: renderToString(<div style={{ color: textColor, whiteSpace: 'nowrap', transform: 'translateX(-50%)' }}>{zone.name}</div>)
        });
    }, [zone.name, zoneColor]);


    return <LeafletPolygon positions={poly} pathOptions={{ color: zoneColor, weight: zone.width, fillColor: zoneColor, fillOpacity: zone.opacity, zIndex }}>
        <LeafletMarker position={center} icon={textIcon}></LeafletMarker>
    </LeafletPolygon>;
};

export const ZoneLayers = ({ mode, zIndex }) => {
    const zones = useMapZones();

    switch (mode) {
        case 'ol':
            return zones.map(z => <OLZone key={z.id} zone={z} zIndex={zIndex}></OLZone>);
        case 'leaflet':
            return zones.map(z => <LeafletZone key={z.id} zone={z} zIndex={zIndex}></LeafletZone>);
        default:
            throw new Error('Unknown mode ' + mode);
    }
};
